import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, addDoc, doc, updateDoc, deleteDoc, getDoc, getDocs, query, where, writeBatch } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, authReady } from '../firebase';

const HomeworkContext = createContext();

export const useHomework = () => useContext(HomeworkContext);

export const HomeworkProvider = ({ children }) => {
  const [courses, setCourses] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [tests, setTests] = useState([]);
  const [testQuestions, setTestQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all courses
  const fetchCourses = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'telegramCourses'));
      const coursesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by order field (if exists) otherwise by name
      const sortedData = coursesData.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        // Fallback to name sorting
        return (a.name || '').localeCompare(b.name || '');
      });
      
      setCourses(sortedData);
      setError(null);
    } catch (err) {
      setError('Failed to fetch courses');
      console.error('Error fetching courses:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all chapters for a course
  const fetchChapters = async (courseId) => {
    setLoading(true);
    try {
      const chaptersQuery = query(
        collection(db, 'telegramChapters'), 
        where('courseId', '==', courseId)
      );
      const querySnapshot = await getDocs(chaptersQuery);
      const chaptersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by order field (if exists) otherwise by name
      const sortedData = chaptersData.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        // Fallback to name sorting
        return (a.name || '').localeCompare(b.name || '');
      });
      
      setChapters(sortedData);
      setError(null);
    } catch (err) {
      setError('Failed to fetch chapters');
      console.error('Error fetching chapters:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all topics for a chapter
  const fetchTopics = async (chapterId) => {
    setLoading(true);
    try {
      const topicsQuery = query(
        collection(db, 'telegramTopics'), 
        where('chapterId', '==', chapterId)
      );
      const querySnapshot = await getDocs(topicsQuery);
      const topicsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by order field (if exists) otherwise by name
      const sortedData = topicsData.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        // Fallback to name sorting
        return (a.name || '').localeCompare(b.name || '');
      });
      
      setTopics(sortedData);
      setError(null);
    } catch (err) {
      setError('Failed to fetch topics');
      console.error('Error fetching topics:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all questions for a topic
  const fetchQuestions = async (topicId) => {
    setLoading(true);
    try {
      const questionsQuery = query(
        collection(db, 'telegramQuestions'), 
        where('topicId', '==', topicId)
      );
      const querySnapshot = await getDocs(questionsQuery);
      const questionsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by order field (if exists) otherwise by created date
      const sortedData = questionsData.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        // Fallback to creation date sorting
        const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
        const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
        return dateA - dateB;
      });
      
      setQuestions(sortedData);
      setError(null);
    } catch (err) {
      setError('Failed to fetch questions');
      console.error('Error fetching questions:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all assignments for a student
  const fetchAssignments = async (studentId) => {
    setLoading(true);
    try {
      const assignmentsQuery = query(
        collection(db, 'telegramAssignments'), 
        where('studentId', '==', studentId)
      );
      const querySnapshot = await getDocs(assignmentsQuery);
      const assignmentsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAssignments(assignmentsData);
      setError(null);
      return assignmentsData;
    } catch (err) {
      setError('Failed to fetch assignments');
      console.error('Error fetching assignments:', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Add new course
  const addCourse = async (courseData) => {
    setLoading(true);
    try {
      const docRef = await addDoc(collection(db, 'telegramCourses'), courseData);
      setCourses([...courses, { id: docRef.id, ...courseData }]);
      setError(null);
      return docRef.id;
    } catch (err) {
      setError('Failed to add course');
      console.error('Error adding course:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Similar functions for chapters, topics, questions, and assignments
  const addChapter = async (courseId, chapterData) => {
    setLoading(true);
    try {
      const docRef = await addDoc(collection(db, 'telegramChapters'), {
        ...chapterData,
        courseId
      });
      setChapters([...chapters, { id: docRef.id, courseId, ...chapterData }]);
      setError(null);
      return docRef.id;
    } catch (err) {
      setError('Failed to add chapter');
      console.error('Error adding chapter:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Add topic
  const addTopic = async (chapterId, courseId, topicData, mediaFiles) => {
    setLoading(true);
    try {
      const mediaArray = [];
      
      // Handle multiple media files for topic
      if (mediaFiles && Array.isArray(mediaFiles)) {
        for (const mediaFile of mediaFiles) {
          const timestamp = Date.now();
          const storageRef = ref(storage, `telegram_topics/${timestamp}_${mediaFile.name}`);
          await uploadBytes(storageRef, mediaFile);
          const mediaUrl = await getDownloadURL(storageRef);
          
          // Determine media type from file
          let mediaType = null;
          if (mediaFile.type.includes('image')) {
            mediaType = 'image';
          } else if (mediaFile.type.includes('video')) {
            mediaType = 'video';
          } else if (mediaFile.type.includes('audio')) {
            mediaType = 'audio';
          }
          
          mediaArray.push({
            url: mediaUrl,
            type: mediaType,
            filename: mediaFile.name
          });
        }
      }
      
      const docRef = await addDoc(collection(db, 'telegramTopics'), {
        ...topicData,
        chapterId,
        courseId,
        mediaFiles: mediaArray,
        createdAt: new Date().toISOString()
      });
      
      const newTopic = { 
        id: docRef.id, 
        ...topicData, 
        chapterId, 
        courseId,
        mediaFiles: mediaArray,
        createdAt: new Date().toISOString() 
      };
      
      // Update the topics state with the new topic
      setTopics(prevTopics => [...prevTopics, newTopic]);
      
      // Fetch all topics for this chapter to refresh the list
      fetchTopics(chapterId);
      
      setError(null);
      return docRef.id;
    } catch (err) {
      setError('Failed to add topic');
      console.error('Error adding topic:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Update course
  const updateCourse = async (courseId, updatedData) => {
    setLoading(true);
    try {
      const courseRef = doc(db, 'telegramCourses', courseId);
      await updateDoc(courseRef, {
        ...updatedData,
        updatedAt: new Date().toISOString()
      });
      
      setCourses(courses.map(c => 
        c.id === courseId ? { ...c, ...updatedData } : c
      ));
      
      setError(null);
      return true;
    } catch (err) {
      setError('Failed to update course');
      console.error('Error updating course:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update chapter
  const updateChapter = async (chapterId, updatedData) => {
    setLoading(true);
    try {
      const chapterRef = doc(db, 'telegramChapters', chapterId);
      await updateDoc(chapterRef, {
        ...updatedData,
        updatedAt: new Date().toISOString()
      });
      
      setChapters(chapters.map(c => 
        c.id === chapterId ? { ...c, ...updatedData } : c
      ));
      
      setError(null);
      return true;
    } catch (err) {
      setError('Failed to update chapter');
      console.error('Error updating chapter:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update topic
  const updateTopic = async (topicId, updatedData, mediaFiles) => {
    setLoading(true);
    try {
      const mediaArray = updatedData.mediaFiles || [];
      
      // Handle new media files for topic update
      if (mediaFiles && Array.isArray(mediaFiles)) {
        for (const mediaFile of mediaFiles) {
          const timestamp = Date.now();
          const storageRef = ref(storage, `telegram_topics/${timestamp}_${mediaFile.name}`);
          await uploadBytes(storageRef, mediaFile);
          const mediaUrl = await getDownloadURL(storageRef);
          
          // Determine media type from file
          let mediaType = null;
          if (mediaFile.type.includes('image')) {
            mediaType = 'image';
          } else if (mediaFile.type.includes('video')) {
            mediaType = 'video';
          } else if (mediaFile.type.includes('audio')) {
            mediaType = 'audio';
          }
          
          mediaArray.push({
            url: mediaUrl,
            type: mediaType,
            filename: mediaFile.name
          });
        }
      }
      
      const topicRef = doc(db, 'telegramTopics', topicId);
      const updatePayload = {
        ...updatedData,
        mediaFiles: mediaArray,
        updatedAt: new Date().toISOString()
      };
      
      await updateDoc(topicRef, updatePayload);
      
      setTopics(topics.map(t => 
        t.id === topicId ? { ...t, ...updatePayload } : t
      ));
      
      setError(null);
      return true;
    } catch (err) {
      setError('Failed to update topic');
      console.error('Error updating topic:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update question
  const updateQuestion = async (questionId, updatedData) => {
    setLoading(true);
    try {
      const questionRef = doc(db, 'telegramQuestions', questionId);
      await updateDoc(questionRef, updatedData);
      
      setQuestions(questions.map(q => 
        q.id === questionId ? { ...q, ...updatedData } : q
      ));
      
      setError(null);
      return true;
    } catch (err) {
      setError('Failed to update question');
      console.error('Error updating question:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };
  // Update question order (for reordering)
  const updateQuestionOrder = async (topicId, reorderedQuestions) => {
    setLoading(true);
    try {
      const batch = writeBatch(db);
      
      // Update each question's order in the batch
      reorderedQuestions.forEach((question, index) => {
        const questionRef = doc(db, 'telegramQuestions', question.id);
        batch.update(questionRef, { order: index });
      });
      
      // Commit the batch
      await batch.commit();
      
      // Update local state with a map approach for consistency
      setQuestions(prevQuestions => {
        const updatedQuestionsMap = {};
        reorderedQuestions.forEach((question, index) => {
          updatedQuestionsMap[question.id] = { ...question, order: index };
        });
        return prevQuestions.map(question =>
          updatedQuestionsMap[question.id] ? updatedQuestionsMap[question.id] : question
        );
      });
      
      setError(null);
      return true;
    } catch (err) {
      setError('Failed to update question order');
      console.error('Error updating question order:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };
  // Update test question order (for reordering)
  const updateTestQuestionOrder = async (testId, reorderedQuestions) => {
    setLoading(true);
    try {
      const batch = writeBatch(db);
      
      // Update each question's order in the batch
      reorderedQuestions.forEach((question, index) => {
        const questionRef = doc(db, 'telegramTestQuestions', question.id);
        batch.update(questionRef, { order: index });
      });
      
      // Commit the batch
      await batch.commit();
      
      // Update local state
      setTestQuestions(reorderedQuestions.map((question, index) => ({
        ...question,
        order: index
      })));
      
      setError(null);
      return true;
    } catch (err) {
      setError('Failed to update test question order');
      console.error('Error updating test question order:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update course order (for reordering)
  const updateCourseOrder = async (reorderedCourses) => {
    setLoading(true);
    try {
      const batch = writeBatch(db);
      
      console.log('Updating course order in Firebase:', reorderedCourses.map((c, i) => ({ id: c.id, name: c.name, order: i })));
      
      // Update each course's order in the batch
      reorderedCourses.forEach((course, index) => {
        const courseRef = doc(db, 'telegramCourses', course.id);
        batch.update(courseRef, { order: index });
      });
      
      // Commit the batch
      await batch.commit();
      
      // Update local state with a map approach for consistency
      setCourses(prevCourses => {
        // Create a map of updated courses by ID
        const updatedCoursesMap = {};
        reorderedCourses.forEach((course, index) => {
          updatedCoursesMap[course.id] = { ...course, order: index };
        });
        
        // Update all courses that were reordered
        return prevCourses.map(course => 
          updatedCoursesMap[course.id] ? updatedCoursesMap[course.id] : course
        );
      });
      
      setError(null);
      return true;
    } catch (err) {
      setError('Failed to update course order');
      console.error('Error updating course order:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  // Update chapter order (for reordering)
  const updateChapterOrder = async (courseId, reorderedChapters) => {
    setLoading(true);
    try {
      const batch = writeBatch(db);
      
      // Update each chapter's order in the batch
      reorderedChapters.forEach((chapter, index) => {
        const chapterRef = doc(db, 'telegramChapters', chapter.id);
        batch.update(chapterRef, { order: index });
      });
      
      // Commit the batch
      await batch.commit();
      
      // Update local state - only update chapters for this course
      setChapters(prevChapters => {
        // Create a map of updated chapters by ID
        const updatedChaptersMap = {};
        reorderedChapters.forEach((chapter, index) => {
          updatedChaptersMap[chapter.id] = { ...chapter, order: index };
        });
        
        // Return updated array where chapters for this course are updated
        return prevChapters.map(chapter => 
          chapter.courseId === courseId && updatedChaptersMap[chapter.id]
            ? updatedChaptersMap[chapter.id]
            : chapter
        );
      });
      
      setError(null);
      return true;
    } catch (err) {
      setError('Failed to update chapter order');
      console.error('Error updating chapter order:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  // Update topic order (for reordering)
  const updateTopicOrder = async (chapterId, reorderedTopics) => {
    setLoading(true);
    try {
      const batch = writeBatch(db);
      
      // Update each topic's order in the batch
      reorderedTopics.forEach((topic, index) => {
        const topicRef = doc(db, 'telegramTopics', topic.id);
        batch.update(topicRef, { order: index });
      });
      
      // Commit the batch
      await batch.commit();
      
      // Update local state - only update topics for this chapter
      setTopics(prevTopics => {
        // Create a map of updated topics by ID
        const updatedTopicsMap = {};
        reorderedTopics.forEach((topic, index) => {
          updatedTopicsMap[topic.id] = { ...topic, order: index };
        });
        
        // Return updated array where topics for this chapter are updated
        return prevTopics.map(topic => 
          topic.chapterId === chapterId && updatedTopicsMap[topic.id]
            ? updatedTopicsMap[topic.id]
            : topic
        );
      });
      
      setError(null);
      return true;
    } catch (err) {
      setError('Failed to update topic order');
      console.error('Error updating topic order:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Add question with multiple media support
  const addQuestion = async (topicId, questionData, mediaFiles) => {
    setLoading(true);
    try {
      const mediaArray = [];
      
      // First, fetch the topic to check if it has media attached
      const topicDoc = await getDoc(doc(db, 'telegramTopics', topicId));
      const topicData = topicDoc.exists() ? topicDoc.data() : null;
      
      // If topic has media, add it to the question's media array FIRST
      if (topicData && topicData.mediaFiles && Array.isArray(topicData.mediaFiles)) {
        mediaArray.push(...topicData.mediaFiles);
      }
      
      // Handle multiple media files uploaded with the question
      if (mediaFiles && Array.isArray(mediaFiles)) {
        for (const mediaFile of mediaFiles) {
          const timestamp = Date.now();
          const storageRef = ref(storage, `telegram_questions/${timestamp}_${mediaFile.name}`);
          await uploadBytes(storageRef, mediaFile);
          const mediaUrl = await getDownloadURL(storageRef);
          
          // Determine media type from file
          let mediaType = null;
          if (mediaFile.type.includes('image')) {
            mediaType = 'image';
          } else if (mediaFile.type.includes('video')) {
            mediaType = 'video';
          } else if (mediaFile.type.includes('audio')) {
            mediaType = 'audio';
          }
          
          mediaArray.push({
            url: mediaUrl,
            type: mediaType,
            filename: mediaFile.name
          });
        }
      }
      // Support single media file for backward compatibility
      else if (mediaFiles && !Array.isArray(mediaFiles)) {
        const mediaFile = mediaFiles;
        const storageRef = ref(storage, `telegram_questions/${Date.now()}_${mediaFile.name}`);
        await uploadBytes(storageRef, mediaFile);
        const mediaUrl = await getDownloadURL(storageRef);
        
        let mediaType = null;
        if (mediaFile.type.includes('image')) {
          mediaType = 'image';
        } else if (mediaFile.type.includes('video')) {
          mediaType = 'video';
        } else if (mediaFile.type.includes('audio')) {
          mediaType = 'audio';
        }
        
        mediaArray.push({
          url: mediaUrl,
          type: mediaType,
          filename: mediaFile.name
        });
      }
      
      // Get count of existing questions to determine order
      const questionsQuery = query(
        collection(db, 'telegramQuestions'), 
        where('topicId', '==', topicId)
      );
      const questionsSnapshot = await getDocs(questionsQuery);
      const currentQuestionsCount = questionsSnapshot.size;
      
      const questionWithMedia = {
        ...questionData,
        topicId,
        // New array-based media structure
        mediaFiles: mediaArray,
        // Keep legacy fields for backward compatibility
        mediaUrl: mediaArray.length > 0 ? mediaArray[0].url : null,
        mediaType: mediaArray.length > 0 ? mediaArray[0].type : null,
        createdAt: new Date().toISOString(),
        order: currentQuestionsCount // Set order to add at the end
      };
      
      const docRef = await addDoc(collection(db, 'telegramQuestions'), questionWithMedia);
      setQuestions([...questions, { id: docRef.id, ...questionWithMedia }]);
      setError(null);
      return docRef.id;
    } catch (err) {
      setError('Failed to add question');
      console.error('Error adding question:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Add test question
  const addTestQuestion = async (testId, questionData, mediaFiles) => {
    setLoading(true);
    try {
      const mediaArray = [];
      
      // Handle multiple media files
      if (mediaFiles && Array.isArray(mediaFiles)) {
        for (const mediaFile of mediaFiles) {
          const timestamp = Date.now();
          const storageRef = ref(storage, `telegram_tests/${timestamp}_${mediaFile.name}`);
          await uploadBytes(storageRef, mediaFile);
          const mediaUrl = await getDownloadURL(storageRef);
          
          // Determine media type from file
          let mediaType = null;
          if (mediaFile.type.includes('image')) {
            mediaType = 'image';
          } else if (mediaFile.type.includes('video')) {
            mediaType = 'video';
          } else if (mediaFile.type.includes('audio')) {
            mediaType = 'audio';
          }
          
          mediaArray.push({
            url: mediaUrl,
            type: mediaType,
            filename: mediaFile.name
          });
        }
      }
      // Support single media file for backward compatibility
      else if (mediaFiles && !Array.isArray(mediaFiles)) {
        const mediaFile = mediaFiles;
        const storageRef = ref(storage, `telegram_tests/${Date.now()}_${mediaFile.name}`);
        await uploadBytes(storageRef, mediaFile);
        const mediaUrl = await getDownloadURL(storageRef);
        
        let mediaType = null;
        if (mediaFile.type.includes('image')) {
          mediaType = 'image';
        } else if (mediaFile.type.includes('video')) {
          mediaType = 'video';
        } else if (mediaFile.type.includes('audio')) {
          mediaType = 'audio';
        }
        
        mediaArray.push({
          url: mediaUrl,
          type: mediaType,
          filename: mediaFile.name
        });
      }
        // Calculate the order for the new question
      const existingQuestions = testQuestions.filter(q => q.testId === testId);
      const nextOrder = existingQuestions.length > 0 
        ? Math.max(...existingQuestions.map(q => q.order || 0)) + 1 
        : 1;

      const questionWithMedia = {
        ...questionData,
        testId,
        order: nextOrder,
        // New array-based media structure
        mediaFiles: mediaArray,
        // Keep legacy fields for backward compatibility
        mediaUrl: mediaArray.length > 0 ? mediaArray[0].url : null,
        mediaType: mediaArray.length > 0 ? mediaArray[0].type : null,
        createdAt: new Date().toISOString()
      };
      
      const docRef = await addDoc(collection(db, 'telegramTestQuestions'), questionWithMedia);
      setTestQuestions([...testQuestions, { id: docRef.id, ...questionWithMedia }]);
      setError(null);
      return docRef.id;
    } catch (err) {
      setError('Failed to add test question');
      console.error('Error adding test question:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateTestQuestion = async (questionId, updatedData) => {
    setLoading(true);
    try {
      const questionRef = doc(db, 'telegramTestQuestions', questionId);
      await updateDoc(questionRef, updatedData);
      
      setTestQuestions(testQuestions.map(q => 
        q.id === questionId ? { ...q, ...updatedData } : q
      ));
      
      setError(null);
      return true;
    } catch (err) {
      setError('Failed to update test question');
      console.error('Error updating test question:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Delete question
  const deleteQuestion = async (questionId) => {
    setLoading(true);
    try {
      // Get the question to check if it has media
      const questionRef = doc(db, 'telegramQuestions', questionId);
      const questionDoc = await getDoc(questionRef);
      
      if (questionDoc.exists()) {
        const question = questionDoc.data();
        
        // Check for and delete multiple media files (new format)
        if (question.mediaFiles && Array.isArray(question.mediaFiles) && question.mediaFiles.length > 0) {
          for (const mediaFile of question.mediaFiles) {
            try {
              if (mediaFile.url) {
                // Extract the file path from the URL
                const mediaPath = decodeURIComponent(mediaFile.url.split('/o/')[1].split('?')[0]);
                const mediaRef = ref(storage, mediaPath);
                await deleteObject(mediaRef);
              }
            } catch (mediaError) {
              console.error('Error deleting media file:', mediaError);
              // Continue with question deletion even if media deletion fails
            }
          }
        }
        // If the question has a single media file (legacy format), delete it from storage
        else if (question.mediaUrl) {
          try {
            // Extract the file path from the URL
            const mediaPath = decodeURIComponent(question.mediaUrl.split('/o/')[1].split('?')[0]);
            const mediaRef = ref(storage, mediaPath);
            await deleteObject(mediaRef);
          } catch (mediaError) {
            console.error('Error deleting media file:', mediaError);
            // Continue with question deletion even if media deletion fails
          }
        }
      }
      
      await deleteDoc(questionRef);
      setQuestions(questions.filter(q => q.id !== questionId));
      setError(null);
      return true;
    } catch (err) {
      setError('Failed to delete question');
      console.error('Error deleting question:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Delete topic (with cascade option)
  const deleteTopic = async (topicId, cascade = false) => {
    setLoading(true);
    try {
      // Wait for anonymous authentication (all users are anonymous)
      await authReady;

      // Check if there are questions associated with this topic
      const questionsQuery = query(
        collection(db, 'telegramQuestions'),
        where('topicId', '==', topicId)
      );
      const questionsSnapshot = await getDocs(questionsQuery);

      if (!questionsSnapshot.empty && !cascade) {
        // Return info about children
        const childrenCount = questionsSnapshot.size;
        return { 
          success: false, 
          hasChildren: true, 
          childrenCount,
          type: 'questions'
        };
      }
      
      // If cascade is true or no children exist, proceed with deletion
      if (cascade && !questionsSnapshot.empty) {
        // Delete all child questions first
        for (const questionDoc of questionsSnapshot.docs) {
          await deleteQuestion(questionDoc.id);
        }
      }

      await deleteDoc(doc(db, 'telegramTopics', topicId));
      setTopics(topics.filter(topic => topic.id !== topicId));
      return { success: true };
    } catch (err) {
      // Provide more specific error messages
      let errorMessage = 'Failed to delete topic';
      
      if (err.code === 'permission-denied' || err.message?.includes('permission')) {
        errorMessage = 'You do not have permission to delete this topic. Please check your authentication status.';
      } else if (err.code === 'unauthenticated') {
        errorMessage = 'You must be authenticated to delete topics. Please log in and try again.';
      } else {
        errorMessage = err.message || errorMessage;
      }
      
      setError(errorMessage);
      console.error('Error deleting topic:', err);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Delete chapter (with cascade option)
  const deleteChapter = async (chapterId, cascade = false) => {
    setLoading(true);
    try {
      // Wait for anonymous authentication (all users are anonymous)
      await authReady;

      // Check if there are topics associated with this chapter
      const topicsQuery = query(
        collection(db, 'telegramTopics'),
        where('chapterId', '==', chapterId)
      );
      const topicsSnapshot = await getDocs(topicsQuery);

      if (!topicsSnapshot.empty && !cascade) {
        // Return info about children
        const childrenCount = topicsSnapshot.size;
        return { 
          success: false, 
          hasChildren: true, 
          childrenCount,
          type: 'topics'
        };
      }
      
      // If cascade is true or no children exist, proceed with deletion
      if (cascade && !topicsSnapshot.empty) {
        // Delete all child topics first (which will cascade delete their questions)
        for (const topicDoc of topicsSnapshot.docs) {
          await deleteTopic(topicDoc.id, true);
        }
      }

      await deleteDoc(doc(db, 'telegramChapters', chapterId));
      setChapters(chapters.filter(chapter => chapter.id !== chapterId));
      return { success: true };
    } catch (err) {
      // Provide more specific error messages
      let errorMessage = 'Failed to delete chapter';
      
      if (err.code === 'permission-denied' || err.message?.includes('permission')) {
        errorMessage = 'You do not have permission to delete this chapter. Please check your authentication status.';
      } else if (err.code === 'unauthenticated') {
        errorMessage = 'You must be authenticated to delete chapters. Please log in and try again.';
      } else {
        errorMessage = err.message || errorMessage;
      }
      
      setError(errorMessage);
      console.error('Error deleting chapter:', err);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Delete course (with cascade option)
  const deleteCourse = async (courseId, cascade = false) => {
    setLoading(true);
    try {
      // Wait for anonymous authentication (all users are anonymous)
      await authReady;

      // Check if there are chapters associated with this course
      const chaptersQuery = query(
        collection(db, 'telegramChapters'),
        where('courseId', '==', courseId)
      );
      const chaptersSnapshot = await getDocs(chaptersQuery);

      if (!chaptersSnapshot.empty && !cascade) {
        // Return info about children
        const childrenCount = chaptersSnapshot.size;
        return { 
          success: false, 
          hasChildren: true, 
          childrenCount,
          type: 'chapters'
        };
      }
      
      // If cascade is true or no children exist, proceed with deletion
      if (cascade && !chaptersSnapshot.empty) {
        // Delete all child chapters first (which will cascade delete their topics and questions)
        for (const chapterDoc of chaptersSnapshot.docs) {
          await deleteChapter(chapterDoc.id, true);
        }
      }

      await deleteDoc(doc(db, 'telegramCourses', courseId));
      setCourses(courses.filter(course => course.id !== courseId));
      return { success: true };
    } catch (err) {
      // Provide more specific error messages
      let errorMessage = 'Failed to delete course';
      
      if (err.code === 'permission-denied' || err.message?.includes('permission')) {
        errorMessage = 'You do not have permission to delete this course. Please check your authentication status.';
      } else if (err.code === 'unauthenticated') {
        errorMessage = 'You must be authenticated to delete courses. Please log in and try again.';
      } else {
        errorMessage = err.message || errorMessage;
      }
      
      setError(errorMessage);
      console.error('Error deleting course:', err);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };
  
  // Delete assignment
  const deleteAssignment = async (assignmentId) => {
    setLoading(true);
    try {
      await deleteDoc(doc(db, 'telegramAssignments', assignmentId));
      setAssignments(assignments.filter(a => a.id !== assignmentId));
      setError(null);
      return true;
    } catch (err) {
      setError('Failed to delete assignment');
      console.error('Error deleting assignment:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  // Assign homework to a student
  const assignHomework = async (studentId, topicId, courseId, chapterId, assignmentData) => {
    setLoading(true);
    try {      const docRef = await addDoc(collection(db, 'telegramAssignments'), {
        studentId,
        topicId,
        courseId,
        chapterId,
        ...assignmentData,
        status: 'assigned',
        assignedAt: new Date() // Store as Date object instead of ISO string
      });
      
      const newAssignment = {
        id: docRef.id,
        studentId,
        topicId,
        courseId,
        chapterId,
        ...assignmentData,
        status: 'assigned',
        assignedAt: new Date() // Store as Date object instead of ISO string
      };
      
      setAssignments([...assignments, newAssignment]);
      setError(null);
      return docRef.id;
    } catch (err) {
      setError('Failed to assign homework');
      console.error('Error assigning homework:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };
  
  // Load initial library data (excluding tests)
  const loadLibraryData = async () => {
    setLoading(true);
    try {
      // Use the sorted fetch functions instead of direct queries
      await fetchCourses();
      
      // Fetch all chapters (sorted)
      const chaptersSnapshot = await getDocs(collection(db, 'telegramChapters'));
      const chaptersData = chaptersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by order field (if exists) otherwise by name
      const sortedChapters = chaptersData.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        // Fallback to name sorting
        return (a.name || '').localeCompare(b.name || '');
      });
      
      setChapters(sortedChapters);
      
      // Fetch all topics (sorted)
      const topicsSnapshot = await getDocs(collection(db, 'telegramTopics'));
      const topicsData = topicsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by order field (if exists) otherwise by name
      const sortedTopics = topicsData.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        // Fallback to name sorting
        return (a.name || '').localeCompare(b.name || '');
      });
      
      setTopics(sortedTopics);
      
      setError(null);
    } catch (err) {
      setError('Failed to load library data');
      console.error('Error loading library data:', err);
    } finally {
      setLoading(false);
    }
  };

  const deleteTest = async (testId, cascade = false) => {
    setLoading(true);
    try {
      // Check if there are questions associated with this test
      const questionsQuery = query(
        collection(db, 'telegramTestQuestions'),
        where('testId', '==', testId)
      );
      const questionsSnapshot = await getDocs(questionsQuery);

      if (!questionsSnapshot.empty && !cascade) {
        // Return info about children
        const childrenCount = questionsSnapshot.size;
        return { 
          success: false, 
          hasChildren: true, 
          childrenCount,
          type: 'questions'
        };
      }
      
      // If cascade is true or no children exist, proceed with deletion
      if (cascade && !questionsSnapshot.empty) {
        // Delete all child questions first
        for (const questionDoc of questionsSnapshot.docs) {
          await deleteTestQuestion(questionDoc.id);
        }
      }

      await deleteDoc(doc(db, 'telegramTests', testId));
      setTests(tests.filter(test => test.id !== testId));
      return { success: true };
    } catch (err) {
      setError(err.message || 'Failed to delete test');
      console.error('Error deleting test:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const deleteTestQuestion = async (questionId) => {
    setLoading(true);
    try {
      // Get the question to check if it has media
      const questionRef = doc(db, 'telegramTestQuestions', questionId);
      const questionDoc = await getDoc(questionRef);
      
      if (questionDoc.exists()) {
        const question = questionDoc.data();
        
        // Check for and delete multiple media files (new format)
        if (question.mediaFiles && Array.isArray(question.mediaFiles) && question.mediaFiles.length > 0) {
          for (const mediaFile of question.mediaFiles) {
            try {
              if (mediaFile.url) {
                // Extract the file path from the URL
                const mediaPath = decodeURIComponent(mediaFile.url.split('/o/')[1].split('?')[0]);
                const mediaRef = ref(storage, mediaPath);
                await deleteObject(mediaRef);
              }
            } catch (mediaError) {
              console.error('Error deleting media file:', mediaError);
              // Continue with question deletion even if media deletion fails
            }
          }
        }
        // If the question has a single media file (legacy format), delete it from storage
        else if (question.mediaUrl) {
          try {
            // Extract the file path from the URL
            const mediaPath = decodeURIComponent(question.mediaUrl.split('/o/')[1].split('?')[0]);
            const mediaRef = ref(storage, mediaPath);
            await deleteObject(mediaRef);
          } catch (mediaError) {
            console.error('Error deleting media file:', mediaError);
            // Continue with question deletion even if media deletion fails
          }
        }
      }
      
      await deleteDoc(questionRef);
      setTestQuestions(testQuestions.filter(q => q.id !== questionId));
      setError(null);
      return true;
    } catch (err) {
      setError('Failed to delete test question');
      console.error('Error deleting test question:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  // Add a new test
  const addTest = async (testData) => {
    setLoading(true);
    try {
      const docRef = await addDoc(collection(db, 'telegramTests'), {
        ...testData,
        createdAt: new Date().toISOString()
      });
      
      const newTest = {
        id: docRef.id,
        ...testData,
        createdAt: new Date().toISOString()
      };
      
      setTests(prevTests => [...prevTests, newTest]);
      setError(null);
      return docRef.id;
    } catch (err) {
      setError('Failed to add test');
      console.error('Error adding test:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Load all data for initializing the library view including tests
  const loadLibraryDataWithTests = async () => {
    setLoading(true);
    try {
      // Fetch courses and other existing data
      await loadLibraryData();
      
      // Fetch tests (sorted)
      const testsSnapshot = await getDocs(collection(db, 'telegramTests'));
      const testsData = testsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by order field (if exists) otherwise by name
      const sortedTests = testsData.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        // Fallback to name sorting
        return (a.name || '').localeCompare(b.name || '');
      });
      
      setTests(sortedTests);
      
      setError(null);
    } catch (err) {
      setError('Failed to load library data with tests');
      console.error('Error loading library data with tests:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all tests
  const fetchTests = async () => {
    setLoading(true);
    try {
      const testsSnapshot = await getDocs(collection(db, 'telegramTests'));
      const testsData = testsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by order field (if exists) otherwise by name
      const sortedData = testsData.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        // Fallback to name sorting
        return (a.name || '').localeCompare(b.name || '');
      });
      
      setTests(sortedData);
      setError(null);
      return sortedData;
    } catch (err) {
      setError('Failed to fetch tests');
      console.error('Error fetching tests:', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Fetch all test questions for a test
  const fetchTestQuestions = async (testId) => {
    setLoading(true);
    try {
      const questionsQuery = query(
        collection(db, 'telegramTestQuestions'), 
        where('testId', '==', testId)
      );
      const querySnapshot = await getDocs(questionsQuery);
      const questionsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by order field (if exists) otherwise by created date
      const sortedData = questionsData.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        // Fallback to creation date sorting
        const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
        const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
        return dateA - dateB;
      });
      
      setTestQuestions(sortedData);
      setError(null);
      return sortedData;
    } catch (err) {
      setError('Failed to fetch test questions');
      console.error('Error fetching test questions:', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Библиотека домашних заданий — для админки; ученику на сайте она не нужна
  // (домашка идёт через Telegram). Раньше её целиком грузил каждый посетитель.
  useEffect(() => {
    const run = () => { if (localStorage.getItem('isAdmin') === 'true') loadLibraryDataWithTests(); };
    run();
    window.addEventListener('rv-admin-login', run);
    return () => window.removeEventListener('rv-admin-login', run);
  }, []);
  
  const value = {
    courses,
    chapters,
    topics,
    questions,
    assignments,
    tests,
    testQuestions,
    loading,
    error,
    fetchCourses,
    fetchChapters,
    fetchTopics,
    fetchQuestions,
    fetchAssignments,
    fetchTests,
    fetchTestQuestions,
    addCourse,
    addChapter,
    addTopic,
    addQuestion,
    addTest,
    addTestQuestion,
    updateCourse,
    updateChapter,
    updateTopic,
    updateQuestion,
    updateTestQuestion,
    updateQuestionOrder,
    updateTestQuestionOrder,
    updateCourseOrder,
    updateChapterOrder,
    updateTopicOrder,
    deleteQuestion,
    deleteTestQuestion,
    deleteCourse,
    deleteChapter,
    deleteTopic,
    deleteTest,
    deleteAssignment,
    assignHomework,
    loadLibraryData,
    loadLibraryDataWithTests
  };

  return (
    <HomeworkContext.Provider value={value}>
      {children}
    </HomeworkContext.Provider>
  );
};
